import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subscription } from 'rxjs';
import * as Prism from 'prismjs';

import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-dart';
import 'prismjs/components/prism-yaml';

@Component({
  selector: 'app-doc-code-block',
  templateUrl: './doc-code-block.component.html',
  styleUrls: ['./doc-code-block.component.scss'],
})
export class DocCodeBlockComponent implements OnChanges, OnDestroy {
  /** Ruta bajo `/content/snippets/`, p. ej. `web/epago-web-head-script.html` */
  @Input() snippetPath: string | null = null;
  /** Código inline (tiene prioridad si `snippetPath` no se usa) */
  @Input() code: string | null = null;
  /** Lenguaje Prism: `markup`, `javascript`, `css`, `dart`, `yaml` */
  @Input() language: string | null = null;
  @Input() title: string | null = null;

  loading = false;
  error: string | null = null;
  rawCode = '';
  highlighted: SafeHtml | null = null;
  /** Clase `language-*` para Prism y estilos de contraste */
  prismLanguage = 'javascript';

  private sub: Subscription | null = null;
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;

  /** Tras copiar: muestra check + "Copied" y vuelve al icono de copiar. */
  copyFeedback: 'idle' | 'copied' = 'idle';

  constructor(
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
    private readonly clipboard: Clipboard
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['code'] || changes['snippetPath'] || changes['language']) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.copyResetTimer != null) {
      clearTimeout(this.copyResetTimer);
      this.copyResetTimer = null;
    }
  }

  copy(): void {
    if (!this.rawCode) {
      return;
    }
    const ok = this.clipboard.copy(this.rawCode);
    if (!ok) {
      return;
    }
    if (this.copyResetTimer != null) {
      clearTimeout(this.copyResetTimer);
    }
    this.copyFeedback = 'copied';
    this.cdr.markForCheck();
    this.copyResetTimer = setTimeout(() => {
      this.copyFeedback = 'idle';
      this.copyResetTimer = null;
      this.cdr.markForCheck();
    }, 2000);
  }

  private load(): void {
    this.sub?.unsubscribe();
    this.error = null;

    if (this.code != null && this.code !== '') {
      this.applyHighlight(this.code);
      return;
    }

    if (!this.snippetPath) {
      this.rawCode = '';
      this.highlighted = null;
      this.prismLanguage = 'javascript';
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    const url = `/content/snippets/${this.snippetPath}`;
    this.sub = this.http.get(url, { responseType: 'text' }).subscribe({
      next: (text) => {
        this.loading = false;
        this.applyHighlight(text);
      },
      error: () => {
        this.loading = false;
        this.error = 'No se pudo cargar el snippet.';
        this.cdr.markForCheck();
      },
    });
  }

  private applyHighlight(source: string): void {
    this.rawCode = source;
    const lang = this.language ?? this.guessLanguage(this.snippetPath ?? '');
    this.prismLanguage = lang;
    const grammar = (Prism.languages as Record<string, Prism.Grammar>)[lang];
    const html = grammar
      ? Prism.highlight(source, grammar, lang)
      : this.escapeHtml(source);
    this.highlighted = this.sanitizer.bypassSecurityTrustHtml(html);
    this.cdr.markForCheck();
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private guessLanguage(path: string): string {
    const lower = path.toLowerCase();
    if (lower.endsWith('.html')) {
      return 'markup';
    }
    if (lower.endsWith('.js')) {
      return 'javascript';
    }
    if (lower.endsWith('.css')) {
      return 'css';
    }
    if (lower.endsWith('.dart')) {
      return 'dart';
    }
    if (lower.endsWith('.yaml') || lower.endsWith('.yml')) {
      return 'yaml';
    }
    return 'javascript';
  }
}
